import React, { useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { DIAL, POPULAR, EUROPE, WORLD, flagUrl, countryName } from "@/data/countries";

const INPUT = "w-full px-4 py-3 rounded-xl border border-black/10 bg-white text-[#1A1A1A] placeholder-[#5C5C5C]/60 focus:outline-none focus:ring-2 focus:ring-[#345B73]/30 focus:border-[#345B73] transition-all";

const L = {
  it: { placeholder: "Prefisso", search: "Cerca paese o prefisso…", empty: "Nessun paese trovato", popular: "Più comuni", europe: "Europa", world: "Resto del mondo", numberPh: "Numero di telefono" },
  en: { placeholder: "Code", search: "Search country or code…", empty: "No country found", popular: "Most common", europe: "Europe", world: "Rest of the world", numberPh: "Phone number" },
  fr: { placeholder: "Indicatif", search: "Rechercher pays ou indicatif…", empty: "Aucun pays trouvé", popular: "Les plus courants", europe: "Europe", world: "Reste du monde", numberPh: "Numéro de téléphone" },
};

const Flag = ({ iso }) => (
  <img src={flagUrl(iso)} alt="" width={22} height={16} className="rounded-[2px] object-cover flex-shrink-0" loading="lazy" />
);

export const PhoneField = ({ lang = "it", cc, number, onCcChange, onNumberChange }) => {
  const [open, setOpen] = useState(false);
  const t = L[lang] || L.it;

  const renderGroup = (heading, list) => (
    <CommandGroup heading={heading}>
      {list.map((iso) => (
        <CommandItem
          key={iso}
          value={`${countryName(iso, lang)} ${iso} ${DIAL[iso]}`}
          data-testid={`cc-option-${iso}`}
          onSelect={() => { onCcChange(iso); setOpen(false); }}
          className="flex items-center gap-2.5 cursor-pointer"
        >
          <Flag iso={iso} />
          <span className="flex-1 truncate">{countryName(iso, lang)}</span>
          <span className="text-[#5C5C5C] text-sm tabular-nums">{DIAL[iso]}</span>
          {cc === iso && <Check className="w-4 h-4 text-[#1D3557]" />}
        </CommandItem>
      ))}
    </CommandGroup>
  );

  return (
    <div className="flex gap-2 items-stretch">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="phone-cc-trigger"
            className="w-[104px] flex-shrink-0 flex items-center gap-1.5 justify-between px-3 py-3 rounded-xl border border-black/10 bg-white text-[#1A1A1A] focus:outline-none focus:ring-2 focus:ring-[#345B73]/30 focus:border-[#345B73] transition-all"
          >
            {cc ? (
              <span className="flex items-center gap-1.5 min-w-0"><Flag iso={cc} /> <span className="tabular-nums text-sm truncate">{DIAL[cc]}</span></span>
            ) : (
              <span className="text-[#5C5C5C]/70 text-sm truncate">{t.placeholder}</span>
            )}
            <ChevronDown className="w-4 h-4 text-[#5C5C5C] flex-shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[320px]" align="start">
          <Command>
            <CommandInput placeholder={t.search} data-testid="phone-cc-search" />
            <CommandList className="max-h-72">
              <CommandEmpty>{t.empty}</CommandEmpty>
              {renderGroup(t.popular, POPULAR)}
              {renderGroup(t.europe, EUROPE)}
              {renderGroup(t.world, WORLD)}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <input
        data-testid="input-phone"
        type="tel"
        inputMode="tel"
        required
        value={number}
        onChange={(e) => onNumberChange(e.target.value.replace(/[^\d\s]/g, ""))}
        placeholder={t.numberPh}
        className={INPUT + " flex-1 min-w-0"}
      />
    </div>
  );
};
